import React from 'react';
import { Collapse, Empty, Tag } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';

const segmentsOf = (segments) => (Array.isArray(segments) ? segments.filter((seg) => seg?.text) : []);

const TranscriptBody = ({ items, transcript }) => {
  if (items.length === 0) {
    return transcript ? (
      <p className="custom-font m-0 p-4 text-sm leading-7 whitespace-pre-wrap break-words rounded-lg border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
        {transcript}
      </p>
    ) : (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span className="custom-font">رونوشتی موجود نیست</span>} />
    );
  }

  return (
    <div className="custom-font space-y-4">
      {items.map((seg, idx) => {
        const isAgent = Number(seg.speaker_id ?? 1) === 1;
        return (
          // eslint-disable-next-line react/no-array-index-key
          <div key={idx} className={`flex flex-col ${isAgent ? 'items-start' : 'items-end'}`}>
            <div
              className={`p-3 rounded-xl max-w-2xl ${
                isAgent ? 'bg-teal-50 dark:bg-teal-900/20 rounded-tr-none' : 'bg-gray-100 dark:bg-gray-800 rounded-tl-none'
              }`}
            >
              <div className={`flex items-center gap-2 mb-1 ${isAgent ? 'justify-start' : 'justify-end'}`}>
                <span className="font-bold text-xs">{isAgent ? 'اپراتور' : 'مخاطب'}</span>
                {seg.time_range && <span className="text-gray-400 text-xs" dir="ltr">{seg.time_range}</span>}
                {seg.emotion && <Tag color="blue" className="text-xs m-0">{seg.emotion}</Tag>}
              </div>
              <div className="text-sm break-words">{seg.text}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * نمایش گفتگوی رونویسی‌شده به صورت حباب‌های اپراتور/مخاطب؛ در نبود سگمنت، متن ساده رونوشت نمایش داده می‌شود.
 *
 * @param {boolean} collapsible نمایش داخل آکاردئون (پیش‌فرض بسته؛ محتوا فقط پس از باز شدن رندر می‌شود)
 * @param {React.ReactNode} extra دکمه‌های کنار عنوان آکاردئون
 */
const CallTranscriptView = ({ segments, transcript, collapsible = false, extra }) => {
  const items = segmentsOf(segments);

  if (!collapsible) return <TranscriptBody items={items} transcript={transcript} />;

  const agentTurns = items.filter((seg) => Number(seg.speaker_id ?? 1) === 1).length;

  return (
    <Collapse
      className="custom-font call-transcript-accordion rounded-2xl overflow-hidden bg-white dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700/60 shadow-sm"
      bordered={false}
      expandIconPosition="end"
      items={[{
        key: 'transcript',
        label: (
          <div className="flex items-center gap-2.5 flex-wrap">
            <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-600 text-white flex items-center justify-center shadow-sm shrink-0">
              <FileTextOutlined />
            </span>
            <span className="custom-font text-sm font-bold text-gray-800 dark:text-gray-100">متن کامل مکالمه</span>
            {items.length > 0 && (
              <span className="flex items-center gap-1">
                <Tag className="custom-font m-0 text-xs">{items.length.toLocaleString('fa-IR')} بخش</Tag>
                <Tag color="cyan" className="custom-font m-0 text-xs">اپراتور {agentTurns.toLocaleString('fa-IR')}</Tag>
                <Tag className="custom-font m-0 text-xs">مخاطب {(items.length - agentTurns).toLocaleString('fa-IR')}</Tag>
              </span>
            )}
          </div>
        ),
        extra,
        children: <TranscriptBody items={items} transcript={transcript} />,
      }]}
    />
  );
};

export default CallTranscriptView;
